import { useQuery, useQueryClient } from '@tanstack/react-query';
import rolePermissionService from '../services/rolePermissionService';
import permissionService from '../services/permissionService';

const permissionsCollectionName = 'permissions';

export const useAllPermissions = () => {
    return useQuery({
        queryKey: [permissionsCollectionName],
        queryFn: () => permissionService.getAll(),
        staleTime: 1000 * 60 * 60 * 24,
        cacheTime: 1000 * 60 * 60 * 24,
    });
};

export const usePermission = ({ permissionName }) => {
    return useQuery({
        queryKey: ['permission', permissionName],
        queryFn: () => rolePermissionService.checkPermission({ permissionName }),
        enabled: !!permissionName,
        staleTime: 1000 * 60 * 60 * 24, // 24 hours
        cacheTime: 1000 * 60 * 60 * 24, // 24 hours
        retry: 2, // Retry failed requests
        refetchOnWindowFocus: false, // Don't refetch on window focus
    });
};

export const useMultiplePermissions = ({ permissionNames }) => {
    return useQuery({
        queryKey: ['permissions', permissionNames?.sort()],
        queryFn: () => rolePermissionService.checkMultiplePermissions({ permissionNames }),
        enabled: !!permissionNames?.length,
        staleTime: 1000 * 60 * 60 * 24, // 24 hours
        cacheTime: 1000 * 60 * 60 * 24, // 24 hours
        retry: 2,
        refetchOnWindowFocus: false,
    });
};

export const useRolePermissions = ({ roleId }) => {
    return useQuery({
        queryKey: ['rolePermissions', roleId],
        queryFn: () => rolePermissionService.getRolePermissions({ roleId }),
        enabled: !!roleId,
        staleTime: 1000 * 60 * 60 * 24,
        cacheTime: 1000 * 60 * 60 * 24,
    });
};

// Utility function to clear permission cache
export const useClearPermissionCache = () => {
    const queryClient = useQueryClient();
    
    return {
        clearAllPermissions: () => {
            queryClient.invalidateQueries(['permission']);
            queryClient.invalidateQueries(['permissions']);
            queryClient.invalidateQueries(['rolePermissions']);
            rolePermissionService.clearUserRoleCache();
        },
        clearUserRoleCache: (userId) => {
            rolePermissionService.clearUserRoleCache(userId);
            queryClient.invalidateQueries(['permission']);
            queryClient.invalidateQueries(['permissions']);
        }
    };
};